/**
 * Act V — Exile and the Prophets (Isaiah – Malachi).
 *
 * The kingdom that Act IV left divided is now audited. Four chapters: the
 * prophets who arrive to prosecute a prosperous, religious nation; the siege
 * and burning of Jerusalem; half a century of learning how to be God's people
 * in somebody else's empire; and a return that never quite arrives, followed by
 * four hundred years of silence.
 *
 * Chapter ids and verseIds are stable — progress is persisted against them, so
 * they must not be renamed.
 *
 * Scripture quoted inside scenes is the World English Bible (British Edition),
 * public domain, wrapped in *asterisks* for the serif face.
 */

export const ACT_EXILE = {
  id: 'exile',
  title: 'Exile and the Prophets',
  summary: 'The warnings go unheeded, the city falls — and hope is written to the ruins.',
  span: 'Isaiah – Malachi',
  chapters: [
    // ── 1 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-prophets',
      title: 'What Is Required',
      where: 'Amos, Micah, Isaiah',
      era: 'c. 800–700 BC',
      artCaption: 'Ivory panelling in Samaria, and a herdsman standing at the gate',
      scenes: [
        "The prophets are not mainly fortune-tellers. They are prosecutors. Most of what they say is about their own century, not the distant future. Their speeches follow a courtroom shape: here is what God did for you, here is what you agreed to, here is the evidence, here is the sentence. They arrive in the 700s BC, when both Israelite kingdoms are richer and safer than they have been in generations. The nation is busy with religion. They come to say that the religion is not the point.",

        "The first with a book of his own is Amos. He is not a professional. Challenged at Bethel, he says he was never a prophet, and no prophet's son. He kept livestock at Tekoa and worked sycamore figs — nicking each unripe fruit by hand so it would ripen sweet. He is a southerner, sent north into somebody else's country. His complaint is not that Israel has stopped worshipping. The worship is going beautifully. *I hate, I despise your feasts*, God says through him — to people selling the poor for a pair of sandals.",

        "Amos preached in the northern kingdom's richest fifty years, and he is specific about the wealth. Winter houses and summer houses. Beds inlaid with ivory. Wine drunk by the bowlful. Diggers at Samaria have since pulled hundreds of carved ivory panels out of the palace quarter, from exactly that period. The furniture he was shouting about. Amaziah, the priest at Bethel, does not argue. He reports Amos to the king and tells him to go home and preach where somebody pays him. Within forty years Assyria had wiped that kingdom off the map.",

        "Micah came from a lowland village, and he said Jerusalem itself would be ploughed like a field. Saying that in Jerusalem was close to blasphemy. Then that sentence does something almost nothing else in the Bible does. A hundred years later Jeremiah says the same thing and is put on trial for his life. The elders stand up and quote Micah by name in his defence: this man said it too, and King Hezekiah did not have him killed. A dead prophet's line, remembered word for word, keeps a living one alive.",

        "What they want instead is oddly consistent. Amos wants justice to *roll on like rivers, and righteousness like a mighty stream.* Micah climbs a ladder of religious expense — calves, thousands of rams, rivers of oil, a firstborn child — and throws the whole ladder away. *He has shown you, O man, what is good. What does the LORD require of you, but to act justly, to love mercy, and to walk humbly with your God?* None of the three is a ceremony. None can be handed to a priest. None can be bought.",

        "Isaiah is the longest and strangest of them. His call comes in the year King Uzziah died: smoke filling the temple, *Holy, holy, holy*, a live coal against his mouth. Then he answers a question nobody asked him. *Here I am. Send me!* Between the warnings he circles a figure he never names. A servant, despised and rejected, wounded for other people's wrongs, and somehow the one who makes them whole. A copy of Isaiah found by the Dead Sea was written around 125 BC, over a century before Jesus. Chapter 53 is in it.",
      ],
      verseId: 'mic-6-8',
      takeaway: 'God wanted justice and mercy, not ceremony — and promised a suffering servant.',
      questions: [
        {
          id: 'sq-prophets-1',
          question: 'According to Micah 6:8, what does the LORD require?',
          options: [
            'Sacrifices offered daily at the temple',
            'The rebuilding of the walls of Jerusalem',
            'To act justly, to love mercy, and to walk humbly with God',
            'Faithful attendance at all the festivals',
          ],
          answer: 2,
          explanation:
            'Micah asks whether God wants thousands of rams or rivers of oil, and answers with three things that cannot be handed to a priest and cannot be bought.',
        },
        {
          id: 'sq-prophets-2',
          question: 'What did Amos do for a living before he was sent to prophesy?',
          options: [
            'He served as a priest at the sanctuary in Bethel',
            'He kept livestock and worked sycamore figs',
            'He was a scribe in the royal court at Samaria',
            'He was a Levite musician in the Jerusalem temple',
          ],
          answer: 1,
          explanation:
            'Amos 7:14. Working sycamore figs meant nicking each unripe fruit by hand so it would sweeten — manual labour, and seasonal. He insists on it precisely because it disqualifies him from the guild.',
        },
        {
          id: 'sq-prophets-3',
          question: 'What is the prophets’ complaint about Israel’s worship?',
          options: [
            'That it was flourishing while the same worshippers exploited the poor',
            'That it had been abandoned in favour of Assyrian gods',
            'That it was being offered at the wrong sanctuary',
            'That the priests had lost their records of descent',
          ],
          answer: 0,
          explanation:
            'The festivals were well attended and the offerings were generous. That is the problem the prophets name: worship running smoothly alongside injustice, and treated as though it settled the account.',
        },
        {
          id: 'sq-prophets-4',
          question: 'Between his warnings, what kind of figure does Isaiah keep describing?',
          options: [
            'A warrior king who drives Assyria out of the land',
            'A priest who purifies the temple and restores the sacrifices',
            'An angel stationed on the walls of Jerusalem',
            'A servant who is rejected and wounded, and heals others through it',
          ],
          answer: 3,
          explanation:
            'The servant songs run through the second half of Isaiah. The scroll from Qumran, copied roughly a century before Jesus was born, shows the text was in circulation long before anyone claimed it had been fulfilled.',
        },
      ],
    },

    // ── 2 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-fall-jerusalem',
      title: 'The City Falls',
      where: '2 Kings 25; Lamentations',
      era: '586 BC',
      artCaption: "The wall breached, the city burned, and someone left sitting in it",
      scenes: [
        "It does not happen all at once. Babylon takes Jerusalem three times in twenty years. In 605 BC it carries off hostages from the best families, Daniel among them. In 597 it comes back for the young king Jehoiachin, the craftsmen and the treasury, and puts his uncle Zedekiah on the throne instead — a king of Nebuchadnezzar's own choosing. Zedekiah then spends nine years being warned by Jeremiah not to rebel. Then he rebels. Babylon comes the third time, and this time it does not leave anything standing.",

        "The siege lasts eighteen months. It lifts once when an Egyptian army approaches, and closes again when it withdraws. When the wall is breached there is no food left in the city. Zedekiah slips out through a gate at night and is caught near Jericho. They kill his sons in front of him, and then blind him. The last thing he ever sees is the thing he was made to watch. A month later an officer arrives to burn the temple and the palace. The survivors are marched a thousand miles east.",

        "In 1935 archaeologists dug out the burnt guardroom of the city gate at Lachish. Inside was broken pottery with letters written on it in ink — messages between a Judean officer in the field and his commander. One of them says they are watching for the fire signals of Lachish, because the signals from Azekah can no longer be seen. Jeremiah 34:7 names Lachish and Azekah as the last two fortified cities still holding out. That piece of pottery is a man noticing, in his own handwriting, that the garrison next door has gone dark.",

        "Lamentations is what somebody wrote sitting in the ashes. Five poems, and the shape of them is almost obsessive. The first, second and fourth each run twenty-two stanzas — one for every letter of the Hebrew alphabet, in order. The third does it three times over. This is grief given a frame rigid enough to carry. Then the fifth poem keeps the twenty-two verses and drops the alphabet altogether. The count survives. The ordering does not. Nobody can prove that collapse was deliberate, and it is very hard to read as an accident.",

        "The disaster was not only political. Jerusalem believed God lived in that building, so the building could never fall. Jeremiah spent years at the gate telling people to stop chanting *the temple of the LORD, the temple of the LORD, the temple of the LORD* at him. When it burned, everyone assumed Babylon's gods had beaten Israel's. The prophets had ruled that out in advance. Their answer is harder to live with than defeat. This was not a god losing. It was a sentence, carried out by the God who was meant to protect them.",

        "Then, in the middle poem, the writer turns. *It is because of the LORD's loving kindnesses that we are not consumed, because his compassion doesn't fail. They are new every morning.* Nothing has been rescued. He is describing what he has decided to remember while there is no rescue. Because of the alphabet, the hope arrives in its slot, on schedule, whether he can feel it that morning or not. The history books end as thinly: Jehoiachin is let out of a Babylonian prison after thirty-seven years and given a seat at the king's table.",
      ],
      verseId: 'lam-3-22',
      takeaway: 'Biblical hope is not denial — it is what you hold on to inside the ruin.',
      questions: [
        {
          id: 'sq-lamentations-1',
          question: 'What does the writer of Lamentations say is new every morning?',
          options: [
            'The strength of the city walls',
            'God’s compassions',
            'The temple sacrifices',
            'The songs of Zion',
          ],
          answer: 1,
          explanation:
            'Lamentations 3:22–23 — written in the wreckage of Jerusalem, not after its restoration. Nothing in the poem has improved by the time he says it.',
        },
        {
          id: 'sq-lamentations-2',
          question: 'How is the book of Lamentations constructed?',
          options: [
            'Five poems, most of them alphabetical acrostics',
            'A single continuous prayer of confession',
            'A legal indictment drawn up against Babylon',
            'A collection of letters written by King Zedekiah',
          ],
          answer: 0,
          explanation:
            'Three poems run one stanza per Hebrew letter and the middle one runs three verses per letter. The fifth keeps the count of twenty-two and abandons the alphabet — the form gives way just as the book runs out of answers.',
        },
        {
          id: 'sq-lamentations-3',
          question: 'What did the letters found in the burnt gate at Lachish record?',
          options: [
            'An inventory of the temple treasury before it was taken',
            'Zedekiah’s appeal to Egypt for military help',
            'A census of the households deported to Babylon',
            'Watching for Lachish’s fire signals, because Azekah’s could no longer be seen',
          ],
          answer: 3,
          explanation:
            'Jeremiah 34:7 names Lachish and Azekah as the last two fortresses still holding out. The ostracon is an independent witness from inside the same collapse, written in ink by someone who did not know he was writing history.',
        },
        {
          id: 'sq-lamentations-4',
          question: 'What did the prophets say the burning of the temple meant?',
          options: [
            'That Babylon’s gods had proved stronger than Israel’s',
            'That the covenant with Abraham had been cancelled',
            'That God had carried out a sentence he had warned them about for centuries',
            'That the temple had been built in the wrong place',
          ],
          answer: 2,
          explanation:
            'Everyone in the ancient world read a burnt temple as a defeated god. Israel’s prophets had insisted in advance on the harder reading — that the God of the temple was the one doing it.',
        },
      ],
    },

    // ── 3 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-babylon',
      title: 'Faith in a Foreign Empire',
      where: 'Daniel; Jeremiah 29',
      era: '586–539 BC',
      artCaption: "A furnace on the plain of Dura, and three men who would not bow",
      scenes: [
        "Exile asked a question Israel had never faced. How do you belong to God in a country that does not? The temple was gone, and with it the only place a sacrifice could be offered. The land was a thousand miles behind them. What survived was what you could carry: the sabbath, circumcision, the food laws, and above all the text. One psalm from these years asks how to sing the LORD's song in a foreign land. The answer they arrived at was to read it, and to gather to hear it read.",

        "Daniel and his friends are conscripted into the Babylonian civil service. They are picked on looks and ability, trained for three years, and renamed. The renaming is the sharp end. Hananiah means the LORD is gracious. Mishael means who is what God is. Azariah means the LORD has helped. Daniel means God is my judge. Each of them carries the name of his God inside his own. The new names — Shadrach, Meshach, Abednego, Belteshazzar — point at Babylonian gods instead. It is not paperwork. It is a state writing over who they were.",

        "Jeremiah writes to the exiles from home, with instructions nobody wanted. Build houses. Plant gardens and eat what grows. Marry, have children — you will be here long enough for grandchildren. Seek the good of the city you were dragged to, and pray for it, because your peace sits inside its peace. Then the line everybody quotes without its address. *I know the thoughts that I think towards you, says the LORD, thoughts of peace, and not of evil, to give you hope and a future.* Written to people with seventy years still to serve.",

        "On the plain of Dura, three of them are ordered by King Nebuchadnezzar to bow to a golden image or be thrown into a furnace. They refuse. Their answer is the high point of Old Testament faith. *Our God whom we serve is able to deliver us from the burning fiery furnace … But if not, let it be known to you, O king, that we will not serve your gods.* Everything hangs on *but if not*. If God rescues them they will not bow. If God does not rescue them they will not bow.",

        "There is a detail in Daniel chapter five that sat oddly for centuries. Belshazzar, terrified by writing appearing on a wall, offers Daniel third place in the kingdom. Third, not second. It read like a mistake, until buried records turned up in Iraq naming Belshazzar as the son of King Nabonidus, running Babylon while his father was away. Second place was already taken. He was standing in it. The book is also written in two languages: it switches to Aramaic, the working language of the empire, for the chapters set at court.",

        "Daniel is one of the books Christians openly disagree about. Some read it as visions written in the 500s BC. Others date it to the 160s BC, under a different ruler demanding the same bow. Either way the book works as a manual for living under a power that wants your loyalty. Ezekiel, meanwhile, is by a canal in Babylon with a promise nobody could see coming. *I will take away the stony heart out of your flesh, and I will give you a heart of flesh.* In 539 BC Babylon falls in a night.",
      ],
      verseId: 'dan-3-17',
      takeaway: 'Faithfulness in exile meant serving a hostile city without bowing to it.',
      questions: [
        {
          id: 'sq-daniel-1',
          question: 'What did Jeremiah tell the exiles to do in Babylon?',
          options: [
            'Refuse all work until they were released',
            'Tear down the Babylonian altars',
            'Build houses, plant gardens, and seek the good of the city',
            'Prepare to march home within the year',
          ],
          answer: 2,
          explanation:
            'Jeremiah 29:4–7, immediately before the famous verse 11. The future God had for them ran through seventy years of Babylon rather than around it.',
        },
        {
          id: 'sq-daniel-2',
          question: 'What happened to the four young men’s Hebrew names in Babylon?',
          options: [
            'They were shortened to suit Babylonian pronunciation',
            'They were replaced with names honouring Babylonian gods',
            'They were kept, as a mark of royal favour',
            'They were forbidden to be spoken aloud',
          ],
          answer: 1,
          explanation:
            'Hananiah, Mishael, Azariah and Daniel each carry the name of their God inside their own. Renaming them was not paperwork; it was an attempt to overwrite the one thing the empire could not confiscate.',
        },
        {
          id: 'sq-daniel-3',
          question: 'What makes the three men’s reply to Nebuchadnezzar so striking?',
          options: [
            'They promised the fire would not touch them',
            'They asked for time to consider the order',
            'They offered to bow publicly and pray privately',
            'They refused to bow whether or not God rescued them',
          ],
          answer: 3,
          explanation:
            'Daniel 3:17–18. The weight sits on “but if not”. Their loyalty is not a wager on being spared, which is what makes it loyalty rather than a calculation.',
        },
        {
          id: 'sq-daniel-4',
          question: 'What held Israel’s faith together in exile, with no temple and no land?',
          options: [
            'The sabbath, the scriptures, and gathering to hear them read',
            'A replacement altar built on the plain of Dura',
            'The authority of the exiled king in Babylon',
            'An annual pilgrimage back to the ruins of Jerusalem',
          ],
          answer: 0,
          explanation:
            'Sacrifice was tied to one building, and the building was gone. What could travel was the text and the practices around it — which is roughly where the synagogue comes from, and why it has no altar in it.',
        },
      ],
    },

    // ── 4 ──────────────────────────────────────────────────────────────────
    {
      id: 'ch-silence',
      title: 'The Long Wait',
      where: 'Ezra, Nehemiah, Malachi',
      era: '539–5 BC',
      artCaption: 'A smaller temple, a wall raised in fifty-two days, and a promised sunrise',
      scenes: [
        "In 539 BC Persia takes Babylon and reverses the policy. Assyria and Babylon had broken conquered peoples by moving them. Cyrus sends them home instead, with their gods, and pays towards rebuilding their temples. A clay barrel found at Babylon, now in the British Museum, is his own account of that programme. It does not mention Judah, but it is the same policy Ezra 1 describes arriving there. Isaiah names Cyrus outright and calls him God's shepherd and his anointed — the only foreign king in the Bible given that second word.",

        "They go back in waves across a century. A first party under Zerubbabel, then Ezra some eighty years later, then Nehemiah. The numbers are small. Most of the community stays in Babylon, where it will remain for over a thousand years. They lay the foundation of a second temple and hold a service, and Ezra says the shouting and the weeping could not be told apart. The old men who had seen the first temple were crying at the size of this one. Haggai asks them outright: does it not look like nothing to you?",

        "Nehemiah is the Persian king's cupbearer, the man who tastes the wine for poison. Only someone trusted gets that close. He asks for leave, an escort and a permit for timber, gets all three, and rides into a city with no walls. He inspects them at night, in secret. The rebuild takes fifty-two days against sabotage, half the workforce under arms and the builders working one-handed. His chief opponent, Sanballat, is not only a character in the book. Letters on papyrus from an island in the Nile, in Egypt, name him as governor of Samaria.",

        "The most important thing in either book is a public reading. Ezra stands on a wooden platform by the Water Gate and reads the law aloud from daybreak until midday. The Levites move through the crowd giving the sense, so that the people understood what was read. After generations in Babylon, everyday speech had drifted towards Aramaic, and the text needed explaining as well as reading out. What happens in that square is the beginning of the sermon. The people burst into tears and are told to go and eat, because the day is holy.",

        "Malachi is the last voice, and the name means my messenger — it may be a title rather than a man. The book is built as an argument. God lays a charge, the people answer back — how have we done that? — and he produces the evidence. Six times. The complaint is not that they worship other gods. It is that they are going through the motions. They are bringing him the blind, the lame and the diseased animals, assuming he will not check. Malachi suggests they try offering the same to the governor.",

        "His last promise is a sunrise — *the sun of righteousness will arise with healing in its wings* — and a messenger sent ahead. The Christian Old Testament stops there because of how the books are ordered. The Hebrew Bible has the same books in a different order and ends with Chronicles, on the words *let him go up*. Then four hundred years pass with no prophet on record. Persia gives way to Alexander, then to Rome. The promise sits there until a Gospel opens with a family tree and picks it back up.",
      ],
      verseId: 'mal-4-2',
      takeaway: 'The Old Testament ends unfinished, on a promise and four centuries of silence.',
      questions: [
        {
          id: 'sq-malachi-1',
          question: 'What happened between the end of Malachi and the opening of the Gospels?',
          options: [
            'The temple was destroyed a second time',
            'Roughly four hundred years passed with no recorded prophet',
            'Israel was exiled again, this time to Egypt',
            'The Hebrew scriptures were lost and reconstructed from Greek',
          ],
          answer: 1,
          explanation:
            'The intertestamental period. The world of the Gospels — synagogues, Pharisees, Sadducees, a Greek Bible, a Roman garrison — is built entirely inside that gap, and none of it is named in the Old Testament.',
        },
        {
          id: 'sq-malachi-2',
          question: 'Why did the old men weep when the second temple’s foundation was laid?',
          options: [
            'Persia had forbidden them to roof it',
            'The priests could not prove their descent',
            'They remembered the first temple, and this one was so much smaller',
            'Nehemiah’s walls had been torn down again overnight',
          ],
          answer: 2,
          explanation:
            'Ezra 3 says the weeping and the shouting could not be told apart. The return happened; the restoration the prophets had described did not, and the people who could compare the two knew it.',
        },
        {
          id: 'sq-malachi-3',
          question: 'Which of Nehemiah’s opponents is named as governor of Samaria in papyri found in Egypt?',
          options: [
            'Tobiah the Ammonite',
            'Noadiah the prophetess',
            'Geshem the Arab',
            'Sanballat',
          ],
          answer: 3,
          explanation:
            'Papyri from a Jewish military colony at Elephantine, on an island in the Nile, refer to Sanballat as governor of Samaria and name two of his sons. The Bible’s minor antagonists are sometimes filed in somebody else’s archive.',
        },
        {
          id: 'sq-malachi-4',
          question: 'Why does the Christian Old Testament end with Malachi when the Hebrew Bible does not?',
          options: [
            'The two traditions order the same books differently; the Hebrew Bible ends with Chronicles',
            'Malachi was rejected from the Jewish canon',
            'Malachi was written after the Hebrew collection had already closed',
            'Christian translators added Malachi from a Greek source',
          ],
          answer: 0,
          explanation:
            'Same books, different arrangement. The Christian sequence puts the prophets last so the final words point forward to a coming messenger; the Hebrew sequence closes on a decree to go up and rebuild.',
        },
      ],
    },
  ],
};
